import React from "react";
import { IParticipantEntity, isParticipantModifiedByOI } from "models/Participant.model";
import { styled } from "@mui/material/styles";
import ParticipantCardSummary from "./ParticipantCardSummary";
import Accordion from "components/odl-v2/Accordion/Accordion";
import AccordionDetails from "components/odl-v2/Accordion/AccordionDetails";
import ParticipantCardContact from "./ParticipantCardContact";
import GroupParticipants from "./GroupParticipants";
import Guard from "components/Guard/Guard";
import { useStoreSelector } from "store/hooks";
import { selectParticipantEnableGroups } from "store/domain-data/participant/participant";
import { css } from "@emotion/react";

type Props = {
  participant: IParticipantEntity;
  isShowAsIndividual?: boolean;
  readOnly?: boolean;
  actions?: React.ReactNode;
};

const ParticipantCardDetail: React.FC<Props> = ({ participant, isShowAsIndividual = false, readOnly, actions }) => {
  const isParticipantEnableGroups = useStoreSelector((state) => selectParticipantEnableGroups(state, participant.id));
  const isAnyGroupNames = React.useMemo(() => !!(participant.groupList || participant.groupName), [
    participant.groupList,
    participant.groupName,
  ]);

  const isGroupParticipant = isParticipantEnableGroups && isAnyGroupNames;
  const isModifiedByOI = isParticipantModifiedByOI(participant.modifiedBy || "");

  // Expand participant by default if group participant or created by AI
  const initialIsExpanded = ((isGroupParticipant && !isShowAsIndividual) || isModifiedByOI) && !participant.isDeleted;
  const [isExpanded, setIsExpanded] = React.useState(initialIsExpanded);

  const handleClickToggleExpandParticipant = React.useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  return (
    <StyledAccordion
      $isShowAsIndividual={isShowAsIndividual}
      expanded={isExpanded}
      disableGutters={true}
      data-testid={`ParticipantCardDetail-${participant.id}`}
    >
      <ParticipantCardSummary
        participant={participant}
        readOnly={readOnly}
        isExpanded={isExpanded}
        isShowAsIndividual={isShowAsIndividual}
        actions={actions}
        onClickToggleExpandParticipant={handleClickToggleExpandParticipant}
      />

      <StyledAccordionDetails $isGroupParticipant={isGroupParticipant} $isShowAsIndividual={isShowAsIndividual}>
        <Guard condition={(isGroupParticipant && isShowAsIndividual) || !isGroupParticipant}>
          <ParticipantCardContact participant={participant} />
        </Guard>
        <Guard condition={isGroupParticipant && isAnyGroupNames && !isShowAsIndividual}>
          <GroupParticipants participant={participant} actions={actions} />
        </Guard>
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};

const StyledAccordion = styled(Accordion, {
  shouldForwardProp: (prop) => prop !== "$isShowAsIndividual",
})<{ $isShowAsIndividual?: boolean }>(
  ({ theme, $isShowAsIndividual }) => css`
    ${!$isShowAsIndividual && `border-top: 1px solid ${theme.palette.background.default};`}
  `
);

const StyledAccordionDetails = styled(AccordionDetails, {
  shouldForwardProp: (prop) => !["$isGroupParticipant", "$isShowAsIndividual"].includes(prop.toString()),
})<{
  $isGroupParticipant?: boolean;
  $isShowAsIndividual?: boolean;
}>(
  ({ theme, $isGroupParticipant, $isShowAsIndividual }) => css`
    padding: 0;

    ${(($isGroupParticipant && $isShowAsIndividual) || !$isGroupParticipant) &&
    `border-top: 1px solid ${theme.palette.background.default};`}
  `
);

export default ParticipantCardDetail;
